import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Check,
  Plus,
  X,
  Sparkles,
  Edit2,
  Lock,
  Unlock,
  FileText,
  MessageSquare,
  Paperclip,
  User,
  Calendar,
  Trophy,
  Zap,
  Star,
  Rocket,
} from "lucide-react";
// office_hub in that css is there 4431 to 5110  
/** Templates */
const STAGE_TEMPLATES = {
  "software-dev": [
    { name: "Planning", items: ["Requirements gathering", "Technical specs", "Timeline estimation"] },
    { name: "Design", items: ["UI/UX wireframes", "Database schema", "API design"] },
    { name: "Development", items: ["Setup environment", "Core features", "Unit tests", "Code review"] },
    { name: "Testing", items: ["QA testing", "User acceptance", "Performance testing"] },
    { name: "Deployment", items: ["Production build", "Deploy to staging", "Deploy to production"] },
  ],
  "design-process": [
    { name: "Research", items: ["User interviews", "Competitive analysis", "Requirements"] },
    { name: "Wireframes", items: ["Low-fidelity sketches", "User flow", "Feedback session"] },
    { name: "Design", items: ["High-fidelity mockups", "Design system", "Prototypes"] },
    { name: "Review", items: ["Stakeholder review", "Design QA", "Iterations"] },
    { name: "Handoff", items: ["Developer specs", "Asset export", "Documentation"] },
  ],
  "audit-workflow": [
    { name: "Initial Engagement", items: ["Client meeting", "Scope definition", "Contract signed"] },
    { name: "Planning", items: ["Risk assessment", "Audit plan", "Team assignment"] },
    { name: "Execution", items: ["Field work", "Testing", "Evidence collection"] },
    { name: "Review", items: ["Quality review", "Partner review", "Client discussion"] },
    { name: "Delivery", items: ["Final report", "Management letter", "Follow-up"] },
  ],
  "marketing-campaign": [
    { name: "Strategy", items: ["Goals & KPIs", "Target audience", "Campaign concept"] },
    { name: "Creative", items: ["Content creation", "Design assets", "Copy writing"] },
    { name: "Execution", items: ["Channel setup", "Launch campaign", "Monitor performance"] },
    { name: "Analysis", items: ["Data collection", "Performance review", "Optimization"] },
  ],
};

/**
 * ✅ JS Component
 * Props:
 * - taskId?: string
 * - viewMode?: "compact" | "expanded" | "full" (kept for compatibility)
 * - onChange?: (stages) => void
 */
export const MultiStageChecklistAdvanced = ({
  taskId,
  viewMode = "expanded",
  onChange,
}) => {
  const [stages, setStages] = useState([
    {
      id: "1",
      name: "Planning",
      completed: false,
      expanded: true,
      locked: false,
      color: "#06B6D4",
      items: [
        {
          id: "1-1",
          text: "Requirements gathered",
          completed: false,
          description: "Collect all project requirements from stakeholders",
          assignee: "Sarah",
          attachments: 2,
          comments: 3,
          status: "in-progress",
          subItems: [
            { id: "1-1-a", text: "Client interview scheduled", completed: false },
            { id: "1-1-b", text: "Document requirements", completed: false },
          ],
        },
        { id: "1-2", text: "Stakeholders identified", completed: false, assignee: "Mike", comments: 1 },
        { id: "1-3", text: "Timeline confirmed bhai", completed: false, dueDate: "2024-12-20" },
      ],
    },
    {
      id: "2",
      name: "Development",
      completed: false,
      expanded: false,
      locked: true,
      color: "#3B82F6",
      items: [
        {
          id: "2-1",
          text: "Setup environment",
          completed: false,
          description: "Configure development tools and dependencies",
          subItems: [
            { id: "2-1-a", text: "Install dependencies", completed: false },
            { id: "2-1-b", text: "Configure database", completed: false },
            { id: "2-1-c", text: "Setup CI/CD", completed: false },
          ],
        },
        { id: "2-2", text: "Create API endpoints", completed: false, assignee: "Danish" },
        { id: "2-3", text: "Write unit tests", completed: false, assignee: "Mohammad" },
        { id: "2-4", text: "Code review", completed: false, comments: 2 },
      ],
    },
    {
      id: "3",
      name: "Testing",
      completed: false,
      expanded: false,
      locked: true,
      color: "#8B5CF6",
      items: [
        { id: "3-1", text: "QA testing", completed: false, assignee: "Sarah" },
        { id: "3-2", text: "User acceptance testing", completed: false },
        { id: "3-3", text: "Performance testing", completed: false, attachments: 1 },
      ],
    },
    {
      id: "4",
      name: "Deployment",
      completed: false,
      expanded: false,
      locked: true,
      color: "#10B981",
      items: [
        { id: "4-1", text: "Production build", completed: false },
        { id: "4-2", text: "Deploy to production", completed: false },
        { id: "4-3", text: "Monitor & verify", completed: false },
      ],
    },
  ]);

  const [editingStageName, setEditingStageName] = useState(null);
  const [editingStageValue, setEditingStageValue] = useState("");
  const [addingItemToStage, setAddingItemToStage] = useState(null);
  const [newItemText, setNewItemText] = useState("");
  const [celebratingStage, setCelebratingStage] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showTemplates, setShowTemplates] = useState(false);

  // progress
  const totalItems = stages.reduce((sum, stage) => sum + stage.items.length, 0);
  const completedItems = stages.reduce((sum, stage) => sum + stage.items.filter((i) => i.completed).length, 0);
  const overallProgress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  const completedStages = stages.filter((s) => s.completed).length;

  // stable dependency key (prevents infinite loops)
  const completionKey = useMemo(() => {
    return stages
      .map((s) => s.items.map((i) => (i.completed ? "1" : "0")).join(""))
      .join("|");
  }, [stages]);

  // Auto-progression and unlocking logic
  useEffect(() => {
    const updatedStages = stages.map((stage, index) => {
      const allItemsCompleted =
        stage.items.length > 0 && stage.items.every((item) => item.completed);

      if (allItemsCompleted && !stage.completed) {
        // celebration
        setCelebratingStage(stage.id);
        setShowConfetti(true);
        setTimeout(() => {
          setCelebratingStage(null);
          setShowConfetti(false);
        }, 2000);

        // unlock next
        const nextIndex = index + 1;
        if (nextIndex < stages.length) {
          const unlocked = stages.map((s, i) => {
            if (i === index) return { ...s, completed: true, expanded: false };
            if (i === nextIndex) return { ...s, locked: false, expanded: true };
            return s;
          });
          return unlocked[index];
        }
        return { ...stage, completed: true, expanded: false };
      }

      if (!allItemsCompleted && stage.completed) {
        return { ...stage, completed: false };
      }

      return stage;
    });

    if (JSON.stringify(updatedStages) !== JSON.stringify(stages)) {
      setStages(updatedStages);
      if (onChange) onChange(updatedStages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completionKey]);

  const toggleStageExpanded = (stageId) => {
    setStages((prev) =>
      prev.map((s) => (s.id === stageId ? { ...s, expanded: !s.expanded } : s))
    );
  };

  const toggleItemExpanded = (itemId) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleItemCompleted = (stageId, itemId) => {
    setStages((prev) =>
      prev.map((stage) => {
        if (stage.id !== stageId) return stage;
        return {
          ...stage,
          items: stage.items.map((item) =>
            item.id === itemId ? { ...item, completed: !item.completed } : item
          ),
        };
      })
    );
  };

  const toggleSubItemCompleted = (stageId, itemId, subItemId) => {
    setStages((prev) =>
      prev.map((stage) => {
        if (stage.id !== stageId) return stage;
        return {
          ...stage,
          items: stage.items.map((item) => {
            if (item.id !== itemId || !item.subItems) return item;
            return {
              ...item,
              subItems: item.subItems.map((sub) =>
                sub.id === subItemId ? { ...sub, completed: !sub.completed } : sub
              ),
            };
          }),
        };
      })
    );
  };

  const addStage = () => {
    const id = Date.now().toString();
    const newStage = {
      id,
      name: `Stage ${stages.length + 1}`,
      completed: false,
      expanded: true,
      locked: stages.length > 0,
      color: "#06B6D4",
      items: [],
    };
    setStages((prev) => [...prev, newStage]);
    setEditingStageName(id);
    setEditingStageValue(newStage.name);
  };

  const deleteStage = (stageId) => {
    setStages((prev) => prev.filter((s) => s.id !== stageId));
  };

  const startEditingStageName = (stage) => {
    setEditingStageName(stage.id);
    setEditingStageValue(stage.name);
  };

  const saveEditingStageName = () => {
    if (editingStageName && editingStageValue.trim()) {
      setStages((prev) =>
        prev.map((s) =>
          s.id === editingStageName ? { ...s, name: editingStageValue.trim() } : s
        )
      );
    }
    setEditingStageName(null);
    setEditingStageValue("");
  };

  const addItemToStage = (stageId) => {
    if (!newItemText.trim()) return;

    setStages((prev) =>
      prev.map((stage) => {
        if (stage.id !== stageId) return stage;
        return {
          ...stage,
          items: [
            ...stage.items,
            {
              id: `${stageId}-${Date.now()}`,
              text: newItemText.trim(),
              completed: false,
            },
          ],
        };
      })
    );

    setNewItemText("");
    setAddingItemToStage(null);
  };

  const deleteItem = (stageId, itemId) => {
    setStages((prev) =>
      prev.map((stage) => {
        if (stage.id !== stageId) return stage;
        return { ...stage, items: stage.items.filter((i) => i.id !== itemId) };
      })
    );
  };

  const applyTemplate = (templateKey) => {
    const template = STAGE_TEMPLATES[templateKey];
    if (!template) return;

    const palette = ["#06B6D4", "#3B82F6", "#8B5CF6", "#10B981", "#F59E0B"];

    const newStages = template.map((stage, idx) => ({
      id: String(idx + 1),
      name: stage.name,
      completed: false,
      expanded: idx === 0,
      locked: idx > 0,
      color: palette[idx % palette.length],
      items: stage.items.map((t, j) => ({
        id: `${idx + 1}-${j + 1}`,
        text: t,
        completed: false,
      })),
    }));

    setStages(newStages);
    setShowTemplates(false);
  };

  const getStageProgress = (stage) => {
    if (!stage.items.length) return 0;
    return Math.round(
      (stage.items.filter((i) => i.completed).length / stage.items.length) * 100
    );
  };

  const getStatusClass = (status) => {
    const base = "MultiStageChecklistAdvanced_Class_StatusBadge";
    if (status === "done") return `${base} ${base}__Done`;
    if (status === "in-progress") return `${base} ${base}__Progress`;
    if (status === "blocked") return `${base} ${base}__Blocked`;
    return `${base} ${base}__Default`;
  };

  const getSubItemLetter = (index) => String.fromCharCode(97 + index);

  const templatesUI = [
    { key: "software-dev", name: "Software Development", Icon: Rocket, variant: "blue" },
    { key: "design-process", name: "Design Process", Icon: Star, variant: "purple" },
    { key: "audit-workflow", name: "Audit Workflow", Icon: FileText, variant: "cyan" },
    { key: "marketing-campaign", name: "Marketing Campaign", Icon: Zap, variant: "amber" },
  ];

  return (
    <div className="MultiStageChecklistAdvanced_Class_Root">
      {/* Confetti */}
      <AnimatePresence>
        {showConfetti && (
          <div className="MultiStageChecklistAdvanced_Class_ConfettiWrap">
            {[...Array(50)].map((_, i) => (
              <motion.div
                key={i}
                className="MultiStageChecklistAdvanced_Class_ConfettiDot"
                style={{
                  background: ["#06B6D4", "#3B82F6", "#8B5CF6", "#10B981", "#F59E0B"][i % 5],
                  left: `${Math.random() * 100}%`,
                  top: -20,
                }}
                initial={{ y: -20, rotate: 0, opacity: 1 }}
                animate={{
                  y: window.innerHeight + 20,
                  rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
                  opacity: 0,
                  x: (Math.random() - 0.5) * 200,
                }}
                transition={{ duration: 2 + Math.random() * 2, ease: "easeOut" }}
              />
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Header row */}
      <div className="MultiStageChecklistAdvanced_Class_HeaderRow">
        <motion.div
          className="MultiStageChecklistAdvanced_Class_ProgressCard"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="MultiStageChecklistAdvanced_Class_ProgressTop">
            <div className="MultiStageChecklistAdvanced_Class_ProgressLeft">
              <Sparkles className="MultiStageChecklistAdvanced_Class_IconMd MultiStageChecklistAdvanced_Class_IconCyan" />
              <span className="MultiStageChecklistAdvanced_Class_ProgressTitle">
                Multi-Stage Execution
              </span>
            </div>

            <div className="MultiStageChecklistAdvanced_Class_ProgressRight">
              <span className="MultiStageChecklistAdvanced_Class_ProgressStages">
                {completedStages}/{stages.length} Stages
              </span>
              <div className="MultiStageChecklistAdvanced_Class_ProgressPct">
                <Trophy className="MultiStageChecklistAdvanced_Class_IconSm MultiStageChecklistAdvanced_Class_IconAmber" />
                <span className="MultiStageChecklistAdvanced_Class_ProgressPctText">
                  {overallProgress}%
                </span>
              </div>
            </div>
          </div>

          <div className="MultiStageChecklistAdvanced_Class_BarOuter">
            <motion.div
              className="MultiStageChecklistAdvanced_Class_BarInner"
              initial={{ width: 0 }}
              animate={{ width: `${overallProgress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </motion.div>

        <button
          onClick={() => setShowTemplates((v) => !v)}
          className="MultiStageChecklistAdvanced_Class_TemplatesBtn"
        >
          <Zap className="MultiStageChecklistAdvanced_Class_IconSm" />
          Templates
        </button>
      </div>

      {/* Templates panel */}
      <AnimatePresence>
        {showTemplates && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="MultiStageChecklistAdvanced_Class_TemplatesGrid"
          >
            {templatesUI.map(({ key, name, Icon, variant }) => (
              <button
                key={key}
                onClick={() => applyTemplate(key)}
                className={`MultiStageChecklistAdvanced_Class_TemplateCard MultiStageChecklistAdvanced_Class_TemplateCard__${variant}`}
              >
                <div className="MultiStageChecklistAdvanced_Class_TemplateInner">
                  <div
                    className={`MultiStageChecklistAdvanced_Class_TemplateIconWrap MultiStageChecklistAdvanced_Class_TemplateIconWrap__${variant}`}
                  >
                    <Icon className="MultiStageChecklistAdvanced_Class_IconMd MultiStageChecklistAdvanced_Class_IconWhite" />
                  </div>
                  <div className="MultiStageChecklistAdvanced_Class_TemplateText">
                    <div className="MultiStageChecklistAdvanced_Class_TemplateName">{name}</div>
                    <div
                      className={`MultiStageChecklistAdvanced_Class_TemplateMeta MultiStageChecklistAdvanced_Class_TemplateMeta__${variant}`}
                    >
                      {STAGE_TEMPLATES[key].length} stages
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Timeline */}
      <div className="MultiStageChecklistAdvanced_Class_TimelineWrap">
        <div className="MultiStageChecklistAdvanced_Class_TimelineRow">
          {stages.map((stage, index) => { 
            const progress = getStageProgress(stage);
            const isActive = stage.expanded;
            const isCelebrating = celebratingStage === stage.id;

            const stageBtnBase = "MultiStageChecklistAdvanced_Class_StageBtn";
            const stageBtnState = stage.locked
              ? `${stageBtnBase} ${stageBtnBase}__Locked`
              : `${stageBtnBase} ${stageBtnBase}__Open`;

            return (
              <div key={stage.id} className="MultiStageChecklistAdvanced_Class_TimelineItem">
                <motion.button
                  onClick={() => !stage.locked && toggleStageExpanded(stage.id)}
                  disabled={stage.locked}
                  className={stageBtnState}
                  style={{
                    background: stage.completed
                      ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                      : isActive
                      ? `linear-gradient(135deg, ${stage.color} 0%, ${stage.color}CC 100%)`
                      : "linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)",
                    clipPath:
                      "polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)",
                    marginLeft: index === 0 ? 0 : -20,
                  }}
                  whileHover={!stage.locked ? { scale: 1.02 } : {}}
                  animate={
                    isCelebrating
                      ? {
                          scale: [1, 1.05, 1],
                          boxShadow: [
                            "0 0 0 0 rgba(6, 182, 212, 0.7)",
                            "0 0 0 20px rgba(6, 182, 212, 0)",
                            "0 0 0 0 rgba(6, 182, 212, 0)",
                          ],
                        }
                      : {}
                  }
                  transition={{ duration: 0.5 }}
                >
                  <div className="MultiStageChecklistAdvanced_Class_StageIconBox">
                    {stage.locked ? (
                      <Lock className={`MultiStageChecklistAdvanced_Class_IconSm ${stage.completed ? "MultiStageChecklistAdvanced_Class_IconWhite" : "MultiStageChecklistAdvanced_Class_IconGray"}`} />
                    ) : stage.completed ? (
                      <Check className="MultiStageChecklistAdvanced_Class_IconMd MultiStageChecklistAdvanced_Class_IconWhite" strokeWidth={3} />
                    ) : (
                      <Unlock className={`MultiStageChecklistAdvanced_Class_IconSm ${isActive ? "MultiStageChecklistAdvanced_Class_IconWhite" : "MultiStageChecklistAdvanced_Class_IconDark"}`} />
                    )}
                  </div>

                  <div className="MultiStageChecklistAdvanced_Class_StageInfo">
                    <div
                      className={`MultiStageChecklistAdvanced_Class_StageName ${
                        stage.completed || isActive ? "MultiStageChecklistAdvanced_Class_TextWhite" : "MultiStageChecklistAdvanced_Class_TextDark"
                      }`}
                    >
                      {stage.name}
                    </div>
                    <div
                      className={`MultiStageChecklistAdvanced_Class_StageMeta ${
                        stage.completed || isActive ? "MultiStageChecklistAdvanced_Class_TextWhiteSoft" : "MultiStageChecklistAdvanced_Class_TextMuted"
                      }`}
                    >
                      {progress}% • {stage.items.filter((i) => i.completed).length}/{stage.items.length}
                    </div>
                  </div>

                  {isActive && !stage.completed && !stage.locked && (
                    <motion.div
                      className="MultiStageChecklistAdvanced_Class_StagePulse"
                      style={{ clipPath: "inherit" }}
                      animate={{
                        boxShadow: [
                          `0 0 0 0 ${stage.color}80`,
                          `0 0 0 4px ${stage.color}40`,
                          `0 0 0 0 ${stage.color}80`,
                        ],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}
                </motion.button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stage Content */}
      <div className="MultiStageChecklistAdvanced_Class_StageContentWrap">
        <AnimatePresence mode="popLayout">
          {stages.map((stage) => {
            if (!stage.expanded) return null;

            return (
              <motion.div
                key={stage.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="MultiStageChecklistAdvanced_Class_StageBlock"
              >
                {/* Stage Header */}
                <div className="MultiStageChecklistAdvanced_Class_StageHeader">
                  <div className="MultiStageChecklistAdvanced_Class_StageHeaderLeft">
                    {editingStageName === stage.id ? (
                      <input
                        type="text"
                        value={editingStageValue}
                        onChange={(e) => setEditingStageValue(e.target.value)}
                        onBlur={saveEditingStageName}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEditingStageName();
                          if (e.key === "Escape") {
                            setEditingStageName(null);
                            setEditingStageValue("");
                          }
                        }}
                        autoFocus
                        className="MultiStageChecklistAdvanced_Class_StageEditInput"
                      />
                    ) : (
                      <div className="MultiStageChecklistAdvanced_Class_StageTitleRow">
                        <h3 className="MultiStageChecklistAdvanced_Class_StageTitle">{stage.name}</h3>
                        <button
                          onClick={() => startEditingStageName(stage)}
                          className="MultiStageChecklistAdvanced_Class_StageEditBtn"
                        >
                          <Edit2 className="MultiStageChecklistAdvanced_Class_IconXs" />
                        </button>
                      </div>
                    )}

                    <span className="MultiStageChecklistAdvanced_Class_StageCount">
                      {stage.items.filter((i) => i.completed).length} of {stage.items.length} complete
                    </span>
                  </div>

                  <button
                    onClick={() => deleteStage(stage.id)}
                    className="MultiStageChecklistAdvanced_Class_StageDeleteBtn"
                  >
                    <X className="MultiStageChecklistAdvanced_Class_IconSm" />
                  </button>
                </div>

                {/* Items */}
                <div className="MultiStageChecklistAdvanced_Class_ItemsWrap">
                  {stage.items.map((item) => {
                    const isExpanded = expandedItems.has(item.id);
                    const hasDetails = !!(item.description || item.subItems || item.assignee || item.dueDate);

                    const cardCls = item.completed
                      ? "MultiStageChecklistAdvanced_Class_ItemCard MultiStageChecklistAdvanced_Class_ItemCard__Done"
                      : "MultiStageChecklistAdvanced_Class_ItemCard MultiStageChecklistAdvanced_Class_ItemCard__Open";

                    const checkboxCls = item.completed
                      ? "MultiStageChecklistAdvanced_Class_Checkbox MultiStageChecklistAdvanced_Class_Checkbox__On"
                      : "MultiStageChecklistAdvanced_Class_Checkbox MultiStageChecklistAdvanced_Class_Checkbox__Off";

                    return (
                      <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className={cardCls}
                      >
                        <div className="MultiStageChecklistAdvanced_Class_ItemInner">
                          <div className="MultiStageChecklistAdvanced_Class_ItemRow">
                            <button
                              onClick={() => toggleItemCompleted(stage.id, item.id)}
                              className={checkboxCls}
                            >
                              <AnimatePresence>
                                {item.completed && (
                                  <motion.div
                                    initial={{ scale: 0, rotate: -180 }}
                                    animate={{ scale: 1, rotate: 0 }}
                                    exit={{ scale: 0, rotate: 180 }}
                                  >
                                    <Check className="MultiStageChecklistAdvanced_Class_IconSm MultiStageChecklistAdvanced_Class_IconWhite" strokeWidth={3} />
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </button>

                            <div className="MultiStageChecklistAdvanced_Class_ItemMain">
                              <div className="MultiStageChecklistAdvanced_Class_ItemTop">
                                <div className="MultiStageChecklistAdvanced_Class_ItemTextWrap">
                                  <div className="MultiStageChecklistAdvanced_Class_ItemTitleRow">
                                    <span
                                      className={
                                        item.completed
                                          ? "MultiStageChecklistAdvanced_Class_ItemText MultiStageChecklistAdvanced_Class_ItemText__Done"
                                          : "MultiStageChecklistAdvanced_Class_ItemText MultiStageChecklistAdvanced_Class_ItemText__Open"
                                      }
                                    >
                                      {item.text}
                                    </span>

                                    {item.status && (
                                      <span className={getStatusClass(item.status)}>
                                        {item.status}
                                      </span>
                                    )}
                                  </div>

                                  <div className="MultiStageChecklistAdvanced_Class_ItemMeta">
                                    {item.assignee && (
                                      <span className="MultiStageChecklistAdvanced_Class_ItemMetaChip">
                                        <User className="MultiStageChecklistAdvanced_Class_Icon2xs" />
                                        {item.assignee}
                                      </span>
                                    )}
                                    {item.dueDate && (
                                      <span className="MultiStageChecklistAdvanced_Class_ItemMetaChip">
                                        <Calendar className="MultiStageChecklistAdvanced_Class_Icon2xs" />
                                        {item.dueDate}
                                      </span>
                                    )}
                                    {item.attachments ? (
                                      <span className="MultiStageChecklistAdvanced_Class_ItemMetaChip">
                                        <Paperclip className="MultiStageChecklistAdvanced_Class_Icon2xs" />
                                        {item.attachments}
                                      </span>
                                    ) : null}
                                    {item.comments ? (
                                      <span className="MultiStageChecklistAdvanced_Class_ItemMetaChip">
                                        <MessageSquare className="MultiStageChecklistAdvanced_Class_Icon2xs" />
                                        {item.comments}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>

                                <div className="MultiStageChecklistAdvanced_Class_ItemActions">
                                  {hasDetails && (
                                    <button
                                      onClick={() => toggleItemExpanded(item.id)}
                                      className="MultiStageChecklistAdvanced_Class_IconBtn"
                                    >
                                      {isExpanded ? "▾" : "▸"}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => deleteItem(stage.id, item.id)}
                                    className="MultiStageChecklistAdvanced_Class_IconBtn MultiStageChecklistAdvanced_Class_IconBtn__Danger"
                                  >
                                    <X className="MultiStageChecklistAdvanced_Class_IconSm" />
                                  </button>
                                </div>
                              </div>

                              {/* Expanded */}
                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="MultiStageChecklistAdvanced_Class_ItemExpanded"
                                  >
                                    {item.description && (
                                      <p className="MultiStageChecklistAdvanced_Class_ItemDesc">
                                        {item.description}
                                      </p>
                                    )}

                                    {item.subItems && item.subItems.length > 0 && (
                                      <div className="MultiStageChecklistAdvanced_Class_SubItemsWrap">
                                        {item.subItems.map((sub, idx) => {
                                          const subBoxCls = sub.completed
                                            ? "MultiStageChecklistAdvanced_Class_SubCheckbox MultiStageChecklistAdvanced_Class_SubCheckbox__On"
                                            : "MultiStageChecklistAdvanced_Class_SubCheckbox MultiStageChecklistAdvanced_Class_SubCheckbox__Off";
                                          return (
                                            <div key={sub.id} className="MultiStageChecklistAdvanced_Class_SubItemRow">
                                              <button
                                                onClick={() =>
                                                  toggleSubItemCompleted(stage.id, item.id, sub.id)
                                                }
                                                className={subBoxCls}
                                              >
                                                <AnimatePresence>
                                                  {sub.completed && (
                                                    <motion.div
                                                      initial={{ scale: 0 }}
                                                      animate={{ scale: 1 }}
                                                      exit={{ scale: 0 }}
                                                    >
                                                      <Check className="MultiStageChecklistAdvanced_Class_IconXs MultiStageChecklistAdvanced_Class_IconWhite" strokeWidth={3} />
                                                    </motion.div>
                                                  )}
                                                </AnimatePresence>
                                              </button>

                                              <span className="MultiStageChecklistAdvanced_Class_SubLetter">
                                                {getSubItemLetter(idx)}.
                                              </span>

                                              <span
                                                className={
                                                  sub.completed
                                                    ? "MultiStageChecklistAdvanced_Class_SubText MultiStageChecklistAdvanced_Class_SubText__Done"
                                                    : "MultiStageChecklistAdvanced_Class_SubText MultiStageChecklistAdvanced_Class_SubText__Open"
                                                }
                                              >
                                                {sub.text}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    <div className="MultiStageChecklistAdvanced_Class_ActionBtnsRow">
                                      <button className="MultiStageChecklistAdvanced_Class_ActionBtn MultiStageChecklistAdvanced_Class_ActionBtn__Primary">
                                        <FileText className="MultiStageChecklistAdvanced_Class_IconXs" />
                                        Open
                                      </button>
                                      <button className="MultiStageChecklistAdvanced_Class_ActionBtn">
                                        <MessageSquare className="MultiStageChecklistAdvanced_Class_IconXs" />
                                        Comment
                                      </button>
                                      <button className="MultiStageChecklistAdvanced_Class_ActionBtn">
                                        <Paperclip className="MultiStageChecklistAdvanced_Class_IconXs" />
                                        Attach
                                      </button>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}

                  {/* Add Item */}
                  {addingItemToStage === stage.id ? (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="MultiStageChecklistAdvanced_Class_AddItemBox"
                    >
                      <input
                        type="text"
                        value={newItemText}
                        onChange={(e) => setNewItemText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") addItemToStage(stage.id);
                          if (e.key === "Escape") {
                            setAddingItemToStage(null);
                            setNewItemText("");
                          }
                        }}
                        onBlur={() => {
                          if (newItemText.trim()) addItemToStage(stage.id);
                          else setAddingItemToStage(null);
                        }}
                        placeholder="Add checklist item..."
                        autoFocus
                        className="MultiStageChecklistAdvanced_Class_AddItemInput"
                      />
                    </motion.div>
                  ) : (
                    <button
                      onClick={() => setAddingItemToStage(stage.id)}
                      className="MultiStageChecklistAdvanced_Class_AddItemBtn"
                    >
                      <Plus className="MultiStageChecklistAdvanced_Class_IconSm" />
                      Add item
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Add New Stage */}
      <motion.button
        onClick={addStage}
        className="MultiStageChecklistAdvanced_Class_AddStageBtn"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <Plus className="MultiStageChecklistAdvanced_Class_IconMd" />
        <span>Add New Stage</span>
      </motion.button>
    </div>
  );
};